import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { ControlsWidgetsDataService } from "../../../services/controls-widgets.data.service";
import { Pages } from "@constants/pages/Pages";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { BaseHighchartWidget } from "../BaseHighchartWidget";
import { nameof } from "@methods/jeffs-toolkit";
import { ControlInfoModel } from "@app/controls/models/ControlInfoModel";
import { WidgetFilterKey } from "@app/dashboard/shared/models/WidgetFilterKey";
import { firstValueFrom } from "rxjs";
import { provideTranslocoScope } from "@jsverse/transloco";

@Component({
    selector: "controls-simple-eff-overdue-widget",
    templateUrl: "../highchart-widget.component.html",
    styleUrls: ["../highchart-widget.component.scss"],
    providers: [provideTranslocoScope("dashboard")]
})
export class ControlSimpleEffOverdueWidgetComponent extends BaseHighchartWidget {
    @ViewChild("chartContainer") chartContainer: ElementRef;

    private controlsWidgetsDataService = inject(ControlsWidgetsDataService);
    private pages = inject(Pages);

    constructor() {
        super();

        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.Organization] =
            nameof<ControlInfoModel>((x) => x.organizationId);
        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.BusinessDimension] =
            nameof<ControlInfoModel>((x) => x.businessDimensionIds);
        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.FrameworkDimension] =
            nameof<ControlInfoModel>((x) => x.frameworkDimensionIds);
    }

    protected async getChartConfig() {
        await this.preloadDashboardScope();

        const filters = this.getFilters();
        const data = await firstValueFrom(this.controlsWidgetsDataService
            .getSimpleOverdueWidgetInfo(filters));

        this.config.noData = !data || data.every((x) => x.y === 0);
        if (this.config.noData) {
            return null;
        }

        const mappedData = [
            <any>{
                colorByPoint: true,
                data,
            },
        ];

        const config = this.getBaseChartConfig(null, mappedData);
        config.legend = { enabled: false };
        config.xAxis = {
            type: "category",
        };

        config.yAxis = {
            allowDecimals: false,
            title: {
                text: this.transloco.translate(
                    "dashboard.controlsSimpleEffOverdueWidget.yAxisTitle"
                ),
            },
        };

        config.tooltip = {
            headerFormat: "<b>{point.key}:</b> {point.y}",
            pointFormat: "",
        };

        return config;
    }

    protected async openWorkspace(ev) {
        const filter = this.getFilter("EffOrDI");
        const category = ev.point.name;
        let searchKeywords: SearchKeyword[];

        if (filter == 1) {
            searchKeywords = [
                this.getSearchKeyword(
                    nameof<ControlInfoModel>((c) => c.effectivenessStatus),
                    category
                ),
                this.getSearchKeyword(
                    nameof<ControlInfoModel>((c) => c.isEffOverdue),
                    "Yes"
                ),
            ];
        } else if (filter == 2) {
            searchKeywords = [
                this.getSearchKeyword(
                    nameof<ControlInfoModel>((c) => c.diStatus),
                    category
                ),
                this.getSearchKeyword(
                    nameof<ControlInfoModel>((c) => c.isDiOverdue),
                    "Yes"
                ),
            ];
        }

        searchKeywords.push(this.getSearchKeyword(
            nameof<ControlInfoModel>((c) => c.requiresMonitoring),
            "Yes"
        ));

        const preset = await this.getBasePreset(searchKeywords);
        const pageToOpen = this.pages.ControlOverview;
        this.openPreset(pageToOpen, preset);
    }

    protected async onGlobalOpenClick() {
        const preset = await this.getBasePreset();
        this.openPreset(this.pages.ControlOverview, preset);
    }
}
